import React, { useEffect, useState } from "react";
import { api, ApiError, type Subscription } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

interface SavedSearch {
  id: string;
  name: string;
  filters: { category?: string; city?: string; locality?: string; type?: string; maxRent?: number };
  lastNotifiedAt: string | null;
  createdAt: string;
}

function describeFilters(f: SavedSearch["filters"], t: (k: string, v?: any) => string): string {
  const parts: string[] = [];
  if (f.category) parts.push(f.category === "pg" ? "PG" : f.category === "commercial" ? "Commercial" : "Residential");
  if (f.locality) parts.push(f.locality);
  else if (f.city) parts.push(f.city);
  if (f.maxRent) parts.push(t("tenantSavedSearches.underRent", { amount: `₹${f.maxRent.toLocaleString("en-IN")}` }));
  return parts.join(" · ") || t("tenantSavedSearches.anyListing");
}

export function TenantSavedSearches({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (view: "plans") => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [searches, setSearches] = useState<SavedSearch[] | null>(null);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [city, setCity] = useState("");
  const [locality, setLocality] = useState("");
  const [maxRent, setMaxRent] = useState("");
  const [category, setCategory] = useState<"" | "independent" | "pg" | "commercial">("");
  const [saving, setSaving] = useState(false);

  function load() {
    api.get("/api/saved-searches/mine").then((d) => setSearches(d.searches)).catch(() => setSearches([]));
    api.get("/api/subscription").then(setSub).catch(() => {});
  }
  useEffect(load, []);

  const isPro = sub?.plan === "pro";

  async function save() {
    setSaving(true);
    try {
      await api.post("/api/saved-searches", {
        filters: {
          category: category || undefined,
          city: city.trim() || undefined,
          locality: locality.trim() || undefined,
          maxRent: maxRent.trim() ? Number(maxRent) : undefined,
        },
      });
      toast(t("tenantSavedSearches.savedToast"));
      setShowForm(false);
      setCity("");
      setLocality("");
      setMaxRent("");
      setCategory("");
      load();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.del(`/api/saved-searches/${id}`);
      setSearches((cur) => cur?.filter((s) => s.id !== id) ?? null);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("tenantSavedSearches.title")}</div>
      </div>

      <div className="field-hint" style={{ marginBottom: 14 }}>{t("tenantSavedSearches.hint")}</div>

      {!isPro && (
        <div className="card" style={{ marginBottom: 14, textAlign: "center", padding: "28px 22px" }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "var(--brand-100)",
              color: "var(--brand-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              margin: "0 auto 16px",
            }}
          >
            🔔
          </div>
          <div style={{ fontWeight: 800, fontSize: 15.5, marginBottom: 6 }}>{t("tenantSavedSearches.lockedTitle")}</div>
          <p className="sub" style={{ lineHeight: 1.6, margin: "0 0 20px" }}>
            {t("tenantSavedSearches.proRequiredNote")}
          </p>
          <button className="btn-solid" style={{ width: "100%" }} onClick={() => onNavigate?.("plans")}>
            {t("tenantSavedSearches.upgradeBtn")}
          </button>
        </div>
      )}

      {searches === null ? (
        <div className="spinner" />
      ) : (
        <>
          {searches.length === 0 && !showForm && (
            <div className="empty-state">{t("tenantSavedSearches.emptyState")}</div>
          )}
          {searches.map((s) => (
            <div className="card-row" key={s.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--line)" }}>
              <div>
                <b>{describeFilters(s.filters, t)}</b>
                {s.lastNotifiedAt && <div className="sub" style={{ marginTop: 2 }}>{t("tenantSavedSearches.lastMatched")}</div>}
              </div>
              <button type="button" className="link-btn" onClick={() => remove(s.id)}>
                {t("common.remove")}
              </button>
            </div>
          ))}
        </>
      )}

      {showForm ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="chip-row">
            {(["", "independent", "pg", "commercial"] as const).map((c) => (
              <div key={c || "any"} className={`chip${category === c ? " selected" : ""}`} onClick={() => setCategory(c)}>
                {c === "" ? t("tenantSavedSearches.anyCategory") : c === "pg" ? "PG" : c === "commercial" ? "Commercial" : "Residential"}
              </div>
            ))}
          </div>
          <div className="field">
            <label>{t("tenantSavedSearches.cityLabel")}</label>
            <div className="input-wrap">
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("tenantSavedSearches.cityPlaceholder")} />
            </div>
          </div>
          <div className="field">
            <label>{t("tenantSavedSearches.localityLabel")}</label>
            <div className="input-wrap">
              <input value={locality} onChange={(e) => setLocality(e.target.value)} placeholder={t("tenantSavedSearches.localityPlaceholder")} />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>{t("tenantSavedSearches.maxRentLabel")}</label>
            <div className="input-wrap">
              <input inputMode="numeric" value={maxRent} onChange={(e) => setMaxRent(e.target.value.replace(/\D/g, ""))} placeholder={t("tenantSavedSearches.maxRentPlaceholder")} />
            </div>
          </div>
          <button className="btn-solid btn-sm" style={{ marginTop: 14 }} disabled={saving} onClick={save}>
            {saving ? t("common.saving") : t("common.save")}
          </button>
          <button className="link-btn" style={{ marginTop: 10, marginLeft: 12 }} onClick={() => setShowForm(false)}>
            {t("common.cancel")}
          </button>
        </div>
      ) : (
        (searches?.length ?? 0) < 5 && (
          <button className="btn-outline" style={{ marginTop: 16 }} onClick={() => setShowForm(true)}>
            {t("tenantSavedSearches.addBtn")}
          </button>
        )
      )}
    </>
  );
}
